<template>
  <div :class="['agent-tool-card', `status-${tool.status || 'running'}`]">
    <div class="tool-head" @click="expanded = !expanded">
      <span class="tool-icon">
        <el-icon v-if="tool.status === 'running'" class="spin"><Loading /></el-icon>
        <el-icon v-else-if="tool.status === 'error'" color="#f56c6c"><CircleCloseFilled /></el-icon>
        <el-icon v-else color="#67c23a"><SuccessFilled /></el-icon>
      </span>
      <div class="tool-main">
        <div class="tool-title">{{ title }}</div>
        <div v-if="pathLine" class="tool-path">{{ pathLine }}</div>
        <div v-if="resultLine" class="tool-result">{{ resultLine }}</div>
      </div>
      <span v-if="duration" class="tool-dur">{{ duration }}</span>
      <el-icon class="chev" :class="{ open: expanded }"><ArrowDown /></el-icon>
    </div>
    <div v-if="expanded" class="tool-body">
      <pre v-if="argsText">{{ argsText }}</pre>
      <pre v-if="resultText">{{ resultText }}</pre>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import { ArrowDown, CircleCloseFilled, Loading, SuccessFilled } from '@element-plus/icons-vue'

const TOOL_RUNNING_LABELS = {
  design_strategy: '正在设计内容策略',
  create_daily_task: '正在创建每日调研任务',
  research_pipeline: '正在调研 · 评分 · 选题',
  traffic_deepen: '正在加深流量判断',
  weekly_plan: '正在生成周计划',
  account_review: '正在复盘账号',
  scan_proposals: '正在扫描主动建议',
  auto_exec: '正在半自动执行',
  search_materials: '正在搜索素材',
  generate_article: '正在生成文章',
  review_content: '正在内容审查',
  save_article: '正在保存文章',
  list_recent_tasks: '正在查询任务',
  get_content_matrix: '正在读取内容矩阵',
  list_hot_ips: '正在读取 IP 库',
  content_ops_overview: '正在查看运营总览',
  design_account_strategy: '正在设计账号策略',
  run_golden_flow: '正在跑黄金流程',
  show_matrix: '正在读取内容矩阵',
  show_ip_library: '正在读取 IP 库',
  show_tasks: '正在查询任务',
}

const DONE_LABELS = {
  design_strategy: '设计内容策略',
  create_daily_task: '创建每日调研任务',
  research_pipeline: '调研 · 评分 · 选题',
  traffic_deepen: '流量加深 · 评论洞察',
  weekly_plan: '生成周计划',
  account_review: '账号复盘',
  scan_proposals: '扫描主动建议',
  auto_exec: '半自动执行',
  search_materials: '搜索素材',
  generate_article: '生成并保存文章',
  review_content: '内容审查',
  save_article: '保存文章',
  list_recent_tasks: '查询最近任务',
  get_content_matrix: '读取内容矩阵',
  list_hot_ips: '读取热门 IP',
  content_ops_overview: '内容运营总览',
  design_account_strategy: '设计账号策略',
  run_golden_flow: '启动黄金流程',
  show_matrix: '读取内容矩阵',
  show_ip_library: '读取 IP 库',
  show_tasks: '查询任务',
}

export default {
  name: 'AgentToolCard',
  components: { ArrowDown, CircleCloseFilled, Loading, SuccessFilled },
  props: {
    tool: { type: Object, required: true },
  },
  setup(props) {
    const expanded = ref(false)

    const title = computed(() => {
      const name = props.tool.name || ''
      const running = props.tool.status === 'running'
      return (
        props.tool.label ||
        (running ? TOOL_RUNNING_LABELS[name] : DONE_LABELS[name]) ||
        (running ? `正在执行 ${name}` : name)
      )
    })

    const pathLine = computed(() => {
      const args = props.tool.args || {}
      return (
        String(args.path || args.query || args.topic || args.platform || args.account_id || '').trim() ||
        (Array.isArray(args.keywords) ? args.keywords.slice(0, 4).join(' · ') : '')
      )
    })

    const resultLine = computed(() => {
      if (props.tool.status === 'running') return '进行中…'
      return props.tool.summary || ''
    })

    const duration = computed(() => {
      if (!props.tool.startedAt) return ''
      const end = props.tool.endedAt || Date.now()
      const ms = end - props.tool.startedAt
      return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`
    })

    const argsText = computed(() => {
      const a = props.tool.args
      if (!a || !Object.keys(a).length) return ''
      try {
        return JSON.stringify(a, null, 2)
      } catch {
        return String(a)
      }
    })

    const resultText = computed(() => {
      if (props.tool.result == null) return ''
      try {
        return typeof props.tool.result === 'string'
          ? props.tool.result
          : JSON.stringify(props.tool.result, null, 2)
      } catch {
        return String(props.tool.result)
      }
    })

    return { expanded, title, pathLine, resultLine, duration, argsText, resultText }
  },
}
</script>

<style scoped>
.agent-tool-card {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #fafbfc;
  margin: 8px 0;
  overflow: hidden;
}
.agent-tool-card.status-running {
  border-color: #b3d8ff;
  background: #f0f7ff;
}
.agent-tool-card.status-error {
  border-color: #fbc4c4;
  background: #fff5f5;
}
.tool-head {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  user-select: none;
}
.tool-icon {
  margin-top: 2px;
}
.tool-main {
  flex: 1;
  min-width: 0;
}
.tool-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}
.tool-path,
.tool-result {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tool-dur {
  font-size: 11px;
  color: #a8abb2;
  white-space: nowrap;
}
.chev {
  transition: transform 0.15s;
  color: #c0c4cc;
  margin-top: 2px;
}
.chev.open {
  transform: rotate(180deg);
}
.tool-body {
  border-top: 1px solid #ebeef5;
  padding: 8px 12px;
  max-height: 180px;
  overflow: auto;
}
.tool-body pre {
  margin: 0 0 8px;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-all;
  color: #606266;
}
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
